import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';

interface WonLostDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'won' | 'lost';
  dealTitle: string;
  onConfirm: (reason: string) => Promise<void>;
}

export const WonLostDialog: React.FC<WonLostDialogProps> = ({
  open, onOpenChange, type, dealTitle, onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isWon = type === 'won';

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      await onConfirm(reason);
      setReason('');
      onOpenChange(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-card border-border">
        <DialogDescription className="sr-only">Mark deal as {type}</DialogDescription>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 text-lg font-bold ${isWon ? 'text-emerald-500' : 'text-red-500'}`}>
            {isWon ? '🏆' : <AlertTriangle className="w-5 h-5" />}
            Mark as {isWon ? 'Won' : 'Lost'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            You're marking <span className="font-semibold text-foreground">"{dealTitle}"</span> as{' '}
            <span className={`font-semibold ${isWon ? 'text-emerald-500' : 'text-red-500'}`}>{type}</span>.
          </p>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-foreground">
              {isWon ? 'Win reason' : 'Loss reason'}{!isWon && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              autoFocus
              placeholder={isWon
                ? 'What made this deal successful? (optional)'
                : 'Why was this deal lost? e.g. Budget, Competitor, Timing...'
              }
              value={reason}
              onChange={e => setReason(e.target.value)}
              className="bg-background border-input resize-none"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={(!isWon && !reason.trim()) || submitting}
            className={isWon
              ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
              : 'bg-red-500 hover:bg-red-600 text-white'
            }
          >
            {submitting ? 'Saving...' : `Confirm ${isWon ? 'Won' : 'Lost'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
